<template>
    <component
        :is="tag"
        ref="editableField"
        class="sbs-editable-field"
        :class="classNames"
        :data-placeholder="placeholder"
        contenteditable
        @focus="focusHandler"
        @blur="blurHandler"
        @input="inputHandler"
        @keydown="keydownHandler"
        @paste="pasteHandler" />
</template>

<script>
import {
    isSpecialKeyPressed,
    isValueConvertedToNumber,
    isStyleKeyPressed,
} from '../../utils/validators.js';
import {
    PREVENT_KEYS,
    BLUR_KEYS,
    ERASE_KEYS,
    ARROW_KEYS,
} from '../../utils/keys.js';

// eslint-disable-next-line func-style,max-statements
function parseChildNodesForValueAndLines(childNodes, isOnFreshLine = true) {
    let newValue = '';

    for (let i = 0; i < childNodes.length; i++) {
        const childNode = childNodes[i];

        if (childNode.nodeName === 'BR') {
            // BRs are always line breaks which means the next loop is on a fresh line
            newValue += '\n';
            // eslint-disable-next-line no-param-reassign
            isOnFreshLine = true;
            // eslint-disable-next-line no-continue
            continue;
        }

        // We may or may not need to create a new line
        if (childNode.nodeName === 'DIV' && isOnFreshLine === false) {
            // Divs create new lines for themselves if they aren't already on one
            newValue += '\n';
        }

        // Whether we created a new line or not, we'll use it for this content so the next loop will not be on a fresh line:
        // eslint-disable-next-line no-param-reassign
        isOnFreshLine = false;

        // Add the text content if this is a text node:
        if (childNode.nodeType === 3 && childNode.textContent) {
            newValue += childNode.textContent;
        }

        // If this node has children, get into them as well:
        newValue += parseChildNodesForValueAndLines(childNode.childNodes);
    }

    return newValue;
}

/**
 * Компонент редактируемого текстового поля, используется для ввода текста.
 */
export default {
    name: 'SbsEditableField',
    emits: ['update:modelValue', 'focus', 'blur', 'keydown', 'paste'],
    data() {
        return {
            isFocus: false,
        };
    },
    computed: {
        classNames() {
            return {
                focused: this.isFocus,
            };
        },
    },
    methods: {
        replaceAll(str, search, replacement) {
            return str.split(search).join(replacement);
        },
        getCurrentContent() {
            let content = parseChildNodesForValueAndLines(this.$refs.editableField.childNodes);

            if (content === '\n') {
                content = '';
                this.setContent(content);
            }

            return content;
        },
        setContent(newContent) {
            this.$refs.editableField.innerText = newContent;
        },
        updateContent() {
            /**
             * Событие `input` срабатывает при изменении значения.
             * В качестве параметра - значение после изменения
             */
            this.$emit('update:modelValue', this.getCurrentContent());
        },
        focusHandler(e) {
            /**
             * Событие `focus` срабатывает при фокусе в поле элемента.
             * В качестве параметра - объект события
             */
            this.$emit('focus', e);
            this.isFocus = true;
        },
        blurHandler(e) {
            /**
             * Событие `blur` срабатывает при потере фокуса элементом.
             * В качестве параметра - объект события
             */
            this.$emit('blur', e);
            this.isFocus = false;
        },
        inputHandler() {
            this.updateContent();
        },
        getPastedData(e) {
            let text = '';

            if (window.clipboardData && window.clipboardData.getData) {
                text = window.clipboardData.getData('Text');
            } else if (e.clipboardData && e.clipboardData.getData) {
                text = e.clipboardData.getData('text/plain');
            }

            text = this.replaceAll(text, '\r\n', ' ');
            text = this.replaceAll(text, '\n', ' ');
            text = this.replaceAll(text, '\r', ' ');

            return text;
        },
        pasteHandler(e) {
            e.preventDefault();

            const currentText = this.getCurrentContent();
            const selection = window.getSelection();
            const { anchorOffset, focusOffset } = selection;
            const selectedSymbolCount = Math.abs(anchorOffset - focusOffset);
            const currentTextLength = currentText.length - selectedSymbolCount;
            let pastedText = this.getPastedData(e);
            const pastedTextLength = pastedText.length + currentTextLength;

            if (this.maxLength && pastedTextLength > Number(this.maxLength)) {
                pastedText = pastedText.slice(0, Number(this.maxLength) - currentTextLength);
            }

            window.document.execCommand('insertText', false, pastedText);
            /**
             * Событие `paste` срабатывает при вставке текста.
             * В качестве параметра - объект события
             */
            this.$emit('paste', e);
        },
        keydownHandler(e) {
            if (PREVENT_KEYS.includes(e.key) || isStyleKeyPressed(e)) {
                e.preventDefault();
            }

            if (BLUR_KEYS.includes(e.key)) {
                this.$refs.editableField.blur();

                return;
            }

            if (
                isSpecialKeyPressed(e) ||
                ERASE_KEYS.includes(e.key) ||
                ARROW_KEYS.includes(e.key)
            ) {
                return;
            }

            const value = this.getCurrentContent();
            const hasSelection = Boolean(window.getSelection().toString());

            if (this.maxLength && value.length >= Number(this.maxLength) && !hasSelection) {
                e.preventDefault();

                return;
            }

            /**
             * Событие `keydown` срабатывает при нажатии клавиши.
             * В качестве параметра - объект события
             */
            this.$emit('keydown', e);
        },
    },
    props: {
        /**
         * Максимальное количество символов, которые можно ввести в поле.
         */
        maxLength: {
            type: [Number, String],
            default: null,
            validator: isValueConvertedToNumber,
        },
        /**
         * Выводит текст внутри поля, который исчезает при вводе первого символа.
         */
        placeholder: {
            type: String,
            default: '',
        },
        /**
         * Определяет тег HTML для редактируемого текстового поля
         */
        tag: {
            type: String,
            default: 'div',
        },
        /**
         * Значение, введенное в поле
         * @model
         */
        modelValue: {
            type: [String, Number],
            default: '',
        },
    },
    watch: {
        modelValue(newValue) {
            if (newValue !== this.getCurrentContent()) {
                this.setContent(newValue);
            }
        },
    },
    mounted() {
        this.setContent(this.modelValue);
    },
};
</script>

<style lang="scss">
.sbs-editable-field {
    display: inline;
    word-wrap: break-word;
    outline: none;
    cursor: text;

    &::before {
        display: inline-block;
        font: inherit;
        transition: $trs;
    }

    &:empty {
        display: inline-block;
        min-width: 1px;

        &::before {
            content: attr(data-placeholder);
        }
    }

    &.focused {
        &:empty::before {
            color: $gray;
        }
    }
}
</style>
